import { Checkbox, Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import React from "react";
import { injectIntl, WithIntlProps } from "react-intl";
import { ExamSessionViewModel } from "../../model/ExamSessionViewModel";
import { GetTranslation } from "../../translations/GetTranslation";
import GridTextField from "../common/customGrid/GridTextField";
import DatePicker from "../common/datePicker/DatePicker";
import TranslatedMessage from "../translations/TranslatedMessage";
import { DateTimeFormat } from "../../data/constants";

type ExamSessionFieldsProps = {
  examSession: ExamSessionViewModel;
  onChangeEmailIsValid?: (isEmailValid: boolean) => void;
  onPropertyValueChanges?: (part: Partial<ExamSessionViewModel>) => void;
} & WithIntlProps<any>;

const ExamSessionFields: React.FunctionComponent<ExamSessionFieldsProps> = ({
  examSession,
  intl,
  onPropertyValueChanges,
}) => {
  const handleTextChange =
    (key: keyof ExamSessionViewModel) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      handleChange(key, event.target.value);
  const handleDateChange = (key: keyof ExamSessionViewModel) => (date: Date | null) => {
    if (date) {
      handleChange(key, date);
    }
  };
  const handleChange = (key: keyof ExamSessionViewModel, value: string | string[] | Date | boolean) => {
    let part: Partial<ExamSessionViewModel> = {
      [key]: value,
    };

    if (onPropertyValueChanges) {
      onPropertyValueChanges(part);
    }
  };

  return (
    <>
      <Grid container={true} spacing={4}>
        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyDE")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={handleTextChange("sessionKeyDE")}
          value={examSession.sessionKeyDE}
        />
        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyEN")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={handleTextChange("sessionKeyEN")}
          value={examSession.sessionKeyEN}
        />
        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyFR")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={handleTextChange("sessionKeyFR")}
          value={examSession.sessionKeyFR}
        />
        <GridTextField
          required
          label={GetTranslation(intl, "examSession.field.sessionKeyIT")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={handleTextChange("sessionKeyIT")}
          value={examSession.sessionKeyIT}
        />
      </Grid>
      <Grid container={true} spacing={1}>
        <Grid xs={12} md={8} lg={6} item={true} />
        <Grid xs={12} md={4} lg={6} item={true} />
        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 35 }}>
            <TranslatedMessage id="examSession.field.examSessionDate" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.from" />}
            value={examSession.from}
            onChange={handleDateChange("from")}
            required={true}
          />
        </Grid>
        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.to" />}
            value={examSession.to}
            onChange={handleDateChange("to")}
            required={true}
          />
        </Grid>
        <Grid xs={12} md={4} lg={6} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 35 }}>
            <TranslatedMessage id="examSession.field.signup" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.from" />}
            value={examSession.signupStartDate}
            onChange={handleDateChange("signupStartDate")}
            required={true}
            showInSwissTimeZone
            format={DateTimeFormat}
          />
        </Grid>
        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.to" />}
            value={examSession.signupEndDate}
            onChange={handleDateChange("signupEndDate")}
            required={true}
            showInSwissTimeZone
            format={DateTimeFormat}
          />
        </Grid>
        <Grid xs={12} md={4} lg={6} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 35 }}>
            <TranslatedMessage id="examSession.field.examSession" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.from" />}
            value={examSession.examsStartDate}
            onChange={handleDateChange("examsStartDate")}
          />
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.to" />}
            value={examSession.examsEndDate}
            onChange={handleDateChange("examsEndDate")}
          />
        </Grid>

        <Grid xs={12} md={4} lg={6} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 21 }}>
            <TranslatedMessage id="examSession.field.freeCancellationEndDate" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label=""
            value={examSession.freeCancellationEndDate}
            onChange={handleDateChange("freeCancellationEndDate")}
            required={true}
          />
        </Grid>

        <Grid xs={12} md={6} lg={8} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 35 }}>
            <TranslatedMessage id="examSession.field.correctionPeriod" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.from" />}
            value={examSession.correctionStartDate}
            onChange={handleDateChange("correctionStartDate")}
          />
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.to" />}
            value={examSession.correctionEndDate}
            onChange={handleDateChange("correctionEndDate")}
          />
        </Grid>

        <Grid xs={12} md={4} lg={6} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 21 }}>
            <TranslatedMessage id="examSession.field.gradingConferenceDate" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label=""
            value={examSession.gradingConferenceDate}
            onChange={handleDateChange("gradingConferenceDate")}
            required={true}
          />
        </Grid>

        <Grid xs={12} md={6} lg={8} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 21 }}>
            <TranslatedMessage id="examSession.field.examResultDate" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label=""
            value={examSession.examResultDate}
            onChange={handleDateChange("examResultDate")}
            required={true}
          />
        </Grid>
        <Grid xs={12} md={6} lg={8} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 21 }}>
            <TranslatedMessage id="examSession.field.inspectionSignupEndDate" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label=""
            value={examSession.objectionDate}
            onChange={handleDateChange("objectionDate")}
            required={true}
          />
        </Grid>

        <Grid xs={12} md={6} lg={8} item={true} />

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 35 }}>
            <TranslatedMessage id="examSession.field.inspectionPeriod" />
          </Typography>
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.from" />}
            value={examSession.inspectionStartDate}
            onChange={handleDateChange("inspectionStartDate")}
          />
        </Grid>

        <Grid xs={12} md={2} lg={2} item={true}>
          <DatePicker
            label={<TranslatedMessage id="examSession.field.to" />}
            value={examSession.inspectionEndDate}
            onChange={handleDateChange("inspectionEndDate")}
          />
        </Grid>

        <Grid xs={12} md={4} lg={6} item={true} />

        <Grid container={true} spacing={4}>
         <GridTextField
          label={GetTranslation(intl, "examSession.field.candidatePin")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={handleTextChange("candidatePin")}
          value={examSession.candidatePin}
          />
           <GridTextField
          label={GetTranslation(intl, "examSession.field.expertPin")}
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          onChange={handleTextChange("expertPin")}
          value={examSession.expertPin}
          />
          </Grid>

        <Grid xs={12} md={4} lg={2} item={true}>
          <Typography variant="body2" style={{ paddingTop: 35 }}>
            <TranslatedMessage id="examSession.field.isLife" />
          </Typography>
        </Grid>
        <Grid xs={12} md={4} lg={6} item={true} style={{ display: "flex", alignItems: "end" }}>
          <Checkbox
            disabled={true}
            checked={examSession.isLife}
            onChange={(e) => handleChange("isLife", e.target.checked)}
            style={{margin: 0, padding: 9}}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item={true} />
      </Grid>
    </>
  );
};

ExamSessionFields.defaultProps = {
  onChangeEmailIsValid: () => {},
  onPropertyValueChanges: () => {},
} as Partial<ExamSessionFieldsProps>;

export default injectIntl(ExamSessionFields);
